/* eslint-disable no-use-before-define */
import axios from 'axios';
import { isTokenExpired } from './tokenUtils';

const PROTOCOL = process.env.REACT_APP_API_PROTOCOL || 'https';
const BASE_DOMAIN = process.env.REACT_APP_BASE_DOMAIN || 'me-line.ir';
const CORE_DOMAIN = process.env.REACT_APP_CORE_DOMAIN || 'me-line.ir';

export const BASE_URL = `${PROTOCOL}://${BASE_DOMAIN}`;
export const CORE_URL = `${PROTOCOL}://panel.${CORE_DOMAIN}`;
export const FINANCE_URL = `${PROTOCOL}://finance.${BASE_DOMAIN}`;

export const API_ENDPOINTS = {
    LOGIN: '/api/main/v1/users/login/',
    LOGOUT: '/api/main/v1/users/logout/',
    REFRESH: '/api/main/v1/users/refresh-token/',
    USERS: '/api/main/v1/users/',
    USER_INFO: '/api/main/v1/users/get/',
    UPDATE_INFO: '/api/main/v1/users/update-info/',
    CHANGE_PASSWORD: '/api/main/v1/users/change-password/',
    USER_GROUPS: '/api/main/v1/users/user-groups/',
    FORGET_PASSWORD_OTP: '/api/main/v1/users/forget-password-otp/',
    OTP: '/api/main/v1/users/otp/',
    RESET_PASSWORD: '/api/main/v1/users/reset-password/',
    NOTIFICATIONS: '/api/notif/v1/get-user-notif/',
    UPDATE_NOTIFICATION: '/api/notif/v1/update-user-notif/',
    MONTHLY_BILL: '/api/finance/v1/monthly-bill/',
    GET_ORDERS: '/api/finance/v1/get-orders/',
    GET_BALANCE: '/api/finance/v1/get-balance/',
    WITHDRAW: '/api/finance/v1/withdraw/',
    DEPOSIT: '/api/finance/v1/deposit/',
    INCREASE_BALANCE: '/api/finance/v1/increase-balance/',
    SETTINGS: '/api/setting',
    PRODUCTS: '/api/products',
    CATEGORIES: '/api/category',
    TABLES: '/api/tables',
    WEEKLY_REPORT: '/api/weeklyReport/',
    ORDERS: '/api/orders/',
    ORDERS_REPORT: '/api/ordersReport/',
    CUSTOMERS: 'api/customers',
    NOTIFICATION_POPUP: '/api/popup',
    ADMIN_LOGIN: '/api/main/v1/users/admin-login/',
    INVENTORY: '/api/inventory',
    ITEM_RECIPES: '/api/item-recipes',
    PRODUCTION_METHODS: '/api/production-methods',
    PRODUCTION_METHOD_LINES: '/api/production-method-lines',
};

const DEFAULT_CACHE_TIME = 5 * 60 * 1000;
const SHORT_CACHE_TIME = 30 * 1000;
const FAILED_CACHE_TIME = 60 * 1000;
const cacheStore = new Map();
const errorCacheStore = new Map();
const pendingRequests = new Map();
const groupInstances = new Map();

export const apiCore = axios.create({
    baseURL: CORE_URL,
    headers: { 'Content-Type': 'application/json' },
});

export const apiFinance = axios.create({
    baseURL: FINANCE_URL,
    headers: { 'Content-Type': 'application/json' },
});

export const apiGroup = () => {
    const group = localStorage.getItem('userGroup');
    if (!group) throw new Error('No user group found');
    if (groupInstances.has(group)) return groupInstances.get(group);

    const instance = axios.create({
        baseURL: `${PROTOCOL}://${group}.${BASE_DOMAIN}`,
        headers: { 'Content-Type': 'application/json' },
    });

    attachInterceptors(instance);
    groupInstances.set(group, instance);
    return instance;
};

function getCacheKey(scope, key) {
    const group = localStorage.getItem('userGroup') || 'core';
    return `${group}:${scope}:${key}`;
}

function clearCacheByPrefix(prefix) {
    Array.from(cacheStore.keys()).forEach((key) => {
        if (key.includes(prefix)) cacheStore.delete(key);
    });
    Array.from(errorCacheStore.keys()).forEach((key) => {
        if (key.includes(prefix)) errorCacheStore.delete(key);
    });
}

function normalizeError(error, fallbackMessage = 'Request failed') {
    return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.message ||
        fallbackMessage
    );
}

function getCachedData(cacheKey) {
    const cached = cacheStore.get(cacheKey);
    if (!cached) return null;
    if (cached.expiresAt < Date.now()) {
        cacheStore.delete(cacheKey);
        return null;
    }
    return cached.data;
}

function getCachedError(cacheKey) {
    const cached = errorCacheStore.get(cacheKey);
    if (!cached) return null;
    if (cached.expiresAt < Date.now()) {
        errorCacheStore.delete(cacheKey);
        return null;
    }
    return cached.message;
}

function setCachedData(cacheKey, data, cacheTime) {
    cacheStore.set(cacheKey, {
        data,
        expiresAt: Date.now() + cacheTime,
    });
    errorCacheStore.delete(cacheKey);
}

function setCachedError(cacheKey, message) {
    errorCacheStore.set(cacheKey, {
        message,
        expiresAt: Date.now() + FAILED_CACHE_TIME,
    });
}

async function requestWithCache(cacheKey, requestFn, options = {}) {
    const { cacheTime = DEFAULT_CACHE_TIME, force = false, fallback = null } = options;

    if (!force) {
        const cached = getCachedData(cacheKey);
        if (cached !== null) return cached;

        const cachedError = getCachedError(cacheKey);
        if (cachedError !== null) {
            if (fallback !== null) return fallback;
            throw new Error(cachedError);
        }
    }

    if (pendingRequests.has(cacheKey)) return pendingRequests.get(cacheKey);

    const pending = requestFn()
        .then((response) => {
            const data = response?.data ?? response;
            setCachedData(cacheKey, data, cacheTime);
            return data;
        })
        .catch((error) => {
            const cached = getCachedData(cacheKey);
            if (cached !== null) return cached;
            const message = normalizeError(error);
            setCachedError(cacheKey, message);
            if (fallback !== null) return fallback;
            throw new Error(message);
        })
        .finally(() => {
            pendingRequests.delete(cacheKey);
        });

    pendingRequests.set(cacheKey, pending);
    return pending;
}

async function requestData(requestFn, fallbackMessage) {
    try {
        const response = await requestFn();
        return response?.data ?? response;
    } catch (error) {
        throw new Error(normalizeError(error, fallbackMessage));
    }
}

function invalidateGroupData() {
    clearCacheByPrefix(':group:');
    clearCacheByPrefix(':finance:');
    clearCacheByPrefix(':navbar:');
}

async function callRefreshToken() {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) throw new Error('No refresh token found');

    const res = await axios.post(
        `${CORE_URL}${API_ENDPOINTS.REFRESH}`,
        { refresh: refreshToken },
        {
            headers: {
                'Content-Type': 'application/json',
                Accept: '*/*',
            },
        }
    );

    const newToken = res.data.access || res.data.token;
    if (!newToken) throw new Error('Invalid refresh response');

    localStorage.setItem('token', newToken);
    return newToken;
}

let isLoggingOut = false;

export async function logoutUser(redirect = true) {
    if (isLoggingOut) return;
    isLoggingOut = true;

    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');

    try {
        if (token && refreshToken) {
            await apiCore.post(
                API_ENDPOINTS.LOGOUT,
                {
                    refresh_token: refreshToken,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
        }
    } catch (err) {
        console.warn('Logout request failed or unauthorized — continuing cleanup:', err?.message);
    } finally {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userGroup');
        cacheStore.clear();
        errorCacheStore.clear();
        pendingRequests.clear();
        groupInstances.clear();

        isLoggingOut = false;

        if (redirect) {
            window.location.href = '/login';
        }
    }
}

let isRefreshing = false;
let refreshPromise = null;

function attachInterceptors(instance) {
    instance.interceptors.request.use(
        async (reqConfig) => {
            let access = localStorage.getItem('token');
            const refresh = localStorage.getItem('refreshToken');

            if ((!access || isTokenExpired(access)) && refresh && !isTokenExpired(refresh)) {
                if (!isRefreshing) {
                    isRefreshing = true;
                    refreshPromise = callRefreshToken()
                        .then((newToken) => {
                            localStorage.setItem('token', newToken);
                            isRefreshing = false;
                            return newToken;
                        })
                        .catch((err) => {
                            isRefreshing = false;
                            logoutUser();
                            throw err;
                        });
                }
                access = await refreshPromise;
            }
            const newConfig = { ...reqConfig };
            if (access) {
                newConfig.headers = {
                    ...newConfig.headers,
                    Authorization: `Bearer ${access}`,
                };
            }
            if (typeof FormData !== 'undefined' && newConfig.data instanceof FormData) {
                newConfig.headers = {
                    ...newConfig.headers,
                };
                delete newConfig.headers['Content-Type'];
            }

            return newConfig;
        },
        (error) => Promise.reject(error)
    );

    instance.interceptors.response.use(
        (res) => res,
        async (error) => {
            const originalRequest = error.config;

            if (
                error.response &&
                error.response.status === 401 &&
                !originalRequest.retryRequest &&
                !originalRequest.url.includes(API_ENDPOINTS.LOGIN)
            ) {
                originalRequest.retryRequest = true;
                try {
                    const refresh = localStorage.getItem('refreshToken');
                    if (!refresh || isTokenExpired(refresh)) {
                        logoutUser();
                        throw error;
                    }
                    const newAccess = await callRefreshToken();
                    localStorage.setItem('token', newAccess);
                    const retryConfig = {
                        ...originalRequest,
                        headers: {
                            ...originalRequest.headers,
                            Authorization: `Bearer ${newAccess}`,
                        },
                    };
                    return instance(retryConfig);
                } catch (refreshErr) {
                    logoutUser();
                    throw refreshErr;
                }
            }

            throw error;
        }
    );
}

attachInterceptors(apiCore);
attachInterceptors(apiFinance);

export function getApiGroup() {
    return apiGroup();
}

export async function loginUser(username, password) {
    const data = await requestData(() => apiCore.post(API_ENDPOINTS.LOGIN, { username, password }));
    const { access, refresh, groups } = data;

    localStorage.setItem('token', access);
    localStorage.setItem('refreshToken', refresh);
    if (groups && groups.length) localStorage.setItem('userGroup', groups[0]);
    cacheStore.clear();
    errorCacheStore.clear();
    pendingRequests.clear();

    return data;
}

export const cachedAPI = {
    clearAll: () => {
        cacheStore.clear();
        errorCacheStore.clear();
        pendingRequests.clear();
    },
    invalidateGroupData,
};

export const userAPI = {
    getMe: (options) =>
        requestWithCache(
            getCacheKey('core', 'me'),
            () => apiCore.get(`${API_ENDPOINTS.USERS}me/`),
            options
        ),
    getInfo: (options) =>
        requestWithCache(
            getCacheKey('core', 'user-info'),
            () => apiCore.get(API_ENDPOINTS.USER_INFO),
            options
        ),
    updateInfo: async (data) => {
        const result = await requestData(() => apiCore.patch(API_ENDPOINTS.UPDATE_INFO, data));
        clearCacheByPrefix(':core:user-info');
        clearCacheByPrefix(':navbar:user-info');
        return result;
    },
    changePassword: async (data) => {
        const result = await requestData(() => apiCore.put(API_ENDPOINTS.CHANGE_PASSWORD, data));
        return result;
    },
    getGroups: (options) =>
        requestWithCache(
            getCacheKey('core', 'groups'),
            () => apiCore.get(API_ENDPOINTS.USER_GROUPS),
            options
        ),
    getMembers: (group, options) =>
        requestWithCache(
            getCacheKey('core', `members:${group}`),
            () => apiCore.get(`${API_ENDPOINTS.USERS}member-list/${group}`),
            options
        ),
    getAll: (options) =>
        requestWithCache(
            getCacheKey('core', 'users'),
            () => apiCore.get(API_ENDPOINTS.USERS),
            options
        ),
    getById: (id, options) =>
        requestWithCache(
            getCacheKey('core', `user:${id}`),
            () => apiCore.get(`${API_ENDPOINTS.USERS}${id}/`),
            options
        ),
    update: async (id, data) => {
        const result = await requestData(() => apiCore.patch(`${API_ENDPOINTS.USERS}${id}/`, data));
        clearCacheByPrefix(':core:users');
        clearCacheByPrefix(`:core:user:${id}`);
        return result;
    },
};

export const authAPI = {
    requestPasswordOtp: async (destination) => {
        const data = await requestData(() =>
            apiCore.post(API_ENDPOINTS.FORGET_PASSWORD_OTP, { destination })
        );
        return data;
    },
    validateOtp: async (data) => {
        const result = await requestData(() => apiCore.post(API_ENDPOINTS.OTP, data));
        return result;
    },
    resetPassword: async (data) => {
        const result = await requestData(() => apiCore.post(API_ENDPOINTS.RESET_PASSWORD, data));
        return result;
    },
};

async function getSvgFileFromUrl(url, fileName) {
    const data = await requestData(() => axios.get(url, { responseType: 'blob' }));
    return new File([data], fileName, {
        type: 'image/svg+xml',
    });
}

export const settingsAPI = {
    getSettings: (options) =>
        requestWithCache(
            getCacheKey('group', 'settings'),
            () => apiGroup().get(API_ENDPOINTS.SETTINGS),
            options
        ),
    updateSettings: async (data) => {
        const result = await requestData(() => apiGroup().patch(API_ENDPOINTS.SETTINGS, data));
        clearCacheByPrefix(':group:settings');
        return result;
    },
};

export const productAPI = {
    getAll: (options) =>
        requestWithCache(
            getCacheKey('group', 'products'),
            () => apiGroup().get(API_ENDPOINTS.PRODUCTS),
            options
        ),
    getById: (id, options) =>
        requestWithCache(
            getCacheKey('group', `product:${id}`),
            () => apiGroup().get(`${API_ENDPOINTS.PRODUCTS}/${id}`),
            options
        ),
    create: async (data) => {
        const result = await requestData(() => apiGroup().post(API_ENDPOINTS.PRODUCTS, data));
        clearCacheByPrefix(':group:products');
        return result;
    },
    update: async (id, data) => {
        const result = await requestData(() =>
            apiGroup().patch(`${API_ENDPOINTS.PRODUCTS}/${id}`, data)
        );
        clearCacheByPrefix(':group:products');
        clearCacheByPrefix(`:group:product:${id}`);
        return result;
    },
    remove: async (id) => {
        const result = await requestData(() =>
            apiGroup().delete(`${API_ENDPOINTS.PRODUCTS}/${id}`)
        );
        clearCacheByPrefix(':group:products');
        clearCacheByPrefix(`:group:product:${id}`);
        return result;
    },
};

export const categoryAPI = {
    getAll: (options) =>
        requestWithCache(
            getCacheKey('group', 'categories'),
            () => apiGroup().get(API_ENDPOINTS.CATEGORIES),
            options
        ),
    getById: (id, options) =>
        requestWithCache(
            getCacheKey('group', `category:${id}`),
            () => apiGroup().get(`${API_ENDPOINTS.CATEGORIES}/${id}`),
            options
        ),
    create: async (data) => {
        const result = await requestData(() => apiGroup().post(API_ENDPOINTS.CATEGORIES, data));
        clearCacheByPrefix(':group:categories');
        return result;
    },
    update: async (id, data) => {
        const result = await requestData(() =>
            apiGroup().patch(`${API_ENDPOINTS.CATEGORIES}/${id}`, data)
        );
        clearCacheByPrefix(':group:categories');
        clearCacheByPrefix(`:group:category:${id}`);
        return result;
    },
    remove: async (id) => {
        const result = await requestData(() =>
            apiGroup().delete(`${API_ENDPOINTS.CATEGORIES}/${id}`)
        );
        clearCacheByPrefix(':group:categories');
        clearCacheByPrefix(`:group:category:${id}`);
        return result;
    },
};

export const inventoryAPI = {
    getAll: (options) =>
        requestWithCache(
            getCacheKey('group', 'INVENTORY'),
            () => apiGroup().get(API_ENDPOINTS.INVENTORY),
            options
        ),
    getById: (id, options) =>
        requestWithCache(
            getCacheKey('group', `INVENTORY:${id}`),
            () => apiGroup().get(`${API_ENDPOINTS.INVENTORY}/${id}`),
            options
        ),
    create: async (data) => {
        const result = await requestData(() => apiGroup().post(API_ENDPOINTS.INVENTORY, data));
        clearCacheByPrefix(':group:INVENTORY');
        return result;
    },
    addWaste: async (id, data) => {
        const result = await requestData(() =>
            apiGroup().post(`${API_ENDPOINTS.INVENTORY}/${id}/waste`, data)
        );
        clearCacheByPrefix(':group:INVENTORY');
        return result;
    },
    addPurchase: async (id, data) => {
        const result = await requestData(() =>
            apiGroup().post(`${API_ENDPOINTS.INVENTORY}/${id}/purchase`, data)
        );
        clearCacheByPrefix(':group:INVENTORY');
        return result;
    },
    update: async (id, data) => {
        const result = await requestData(() =>
            apiGroup().patch(`${API_ENDPOINTS.INVENTORY}/${id}`, data)
        );
        clearCacheByPrefix(':group:INVENTORY');
        clearCacheByPrefix(`:group:INVENTORY:${id}`);
        return result;
    },
    remove: async (id) => {
        const result = await requestData(() =>
            apiGroup().delete(`${API_ENDPOINTS.INVENTORY}/${id}`)
        );
        clearCacheByPrefix(':group:INVENTORY');
        clearCacheByPrefix(`:group:INVENTORY:${id}`);
        return result;
    },
};

export const tableAPI = {
    getAll: (options) =>
        requestWithCache(
            getCacheKey('group', 'tables'),
            () => apiGroup().get(API_ENDPOINTS.TABLES),
            options
        ),
    getById: (id, options) =>
        requestWithCache(
            getCacheKey('group', `table:${id}`),
            () => apiGroup().get(`${API_ENDPOINTS.TABLES}/${id}`),
            options
        ),
    create: async (data) => {
        const result = await requestData(() => apiGroup().post(API_ENDPOINTS.TABLES, data));
        clearCacheByPrefix(':group:tables');
        return result;
    },
    update: async (id, data) => {
        const result = await requestData(() =>
            apiGroup().patch(`${API_ENDPOINTS.TABLES}/${id}`, data)
        );
        clearCacheByPrefix(':group:tables');
        clearCacheByPrefix(`:group:table:${id}`);
        return result;
    },
    remove: async (id) => {
        const result = await requestData(() => apiGroup().delete(`${API_ENDPOINTS.TABLES}/${id}`));
        clearCacheByPrefix(':group:tables');
        clearCacheByPrefix(`:group:table:${id}`);
        return result;
    },
};

export const fileAPI = {
    getSvgFileFromUrl,
};

export const navbarAPI = {
    getUserInfo: (options) =>
        requestWithCache(
            getCacheKey('navbar', 'user-info'),
            () => apiCore.get(API_ENDPOINTS.USER_INFO),
            options
        ),
    getNotifications: (options) =>
        requestWithCache(
            getCacheKey('navbar', 'notifications'),
            () => apiCore.get(API_ENDPOINTS.NOTIFICATIONS),
            { cacheTime: SHORT_CACHE_TIME, ...options, fallback: [] }
        ),
    markNotificationAsRead: async (notifUid) => {
        const result = await requestData(() =>
            apiCore.patch(`${API_ENDPOINTS.UPDATE_NOTIFICATION}${notifUid}/`, {
                is_active: false,
            })
        );
        clearCacheByPrefix(':navbar:notifications');
        return result;
    },
    getGroups: (options) => userAPI.getGroups(options),
};

export const financeAPI = {
    getOrders: (options) =>
        requestWithCache(
            getCacheKey('finance', 'orders'),
            () => apiCore.get(API_ENDPOINTS.GET_ORDERS),
            { cacheTime: SHORT_CACHE_TIME, ...options, fallback: [] }
        ),
    getBalance: (options) =>
        requestWithCache(
            getCacheKey('finance', 'balance'),
            () => apiCore.get(API_ENDPOINTS.GET_BALANCE),
            { cacheTime: SHORT_CACHE_TIME, ...options, fallback: { balance: 0 } }
        ),
    withdraw: async (data) => {
        const result = await requestData(() => apiCore.post(API_ENDPOINTS.WITHDRAW, data));
        clearCacheByPrefix(':finance:');
        return result;
    },
    deposit: async (data) => {
        const result = await requestData(() => apiCore.post(API_ENDPOINTS.DEPOSIT, data));
        clearCacheByPrefix(':finance:');
        return result;
    },
    increaseBalance: async (data) => {
        const result = await requestData(() => apiCore.post(API_ENDPOINTS.INCREASE_BALANCE, data));
        clearCacheByPrefix(':finance:balance');
        return result;
    },
    getWeeklyReport: async () => undefined,
    getMonthlyBills: (options) =>
        requestWithCache(
            getCacheKey('finance', 'monthly-bills'),
            () => apiCore.get(API_ENDPOINTS.MONTHLY_BILL),
            { cacheTime: SHORT_CACHE_TIME, ...options, fallback: [] }
        ),
    downloadBill: async (url) => {
        const data = await requestData(() => apiCore.get(url, { responseType: 'blob' }));
        return data;
    },
};

export const ordersAPI = {
    getAll: (options) =>
        requestWithCache(
            getCacheKey('group', 'orders'),
            () => apiGroup().get(API_ENDPOINTS.ORDERS),
            { cacheTime: SHORT_CACHE_TIME, ...options, fallback: [] }
        ),

    getReport: (params = {}, options = {}) => {
        const searchParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value === undefined || value === null || value === '') {
                return;
            }

            if (Array.isArray(value)) {
                value.forEach((item) => {
                    if (item !== undefined && item !== null && item !== '') {
                        searchParams.append(key, String(item));
                    }
                });
            } else {
                searchParams.append(key, String(value));
            }
        });

        const queryString = searchParams.toString();

        return requestWithCache(
            getCacheKey('group', `orders-report:${queryString}`),
            () => apiGroup().get(`${API_ENDPOINTS.ORDERS_REPORT}?${queryString}`),
            {
                cacheTime: SHORT_CACHE_TIME,
                ...options,
                fallback: [],
            }
        );
    },

    getById: async (id) => {
        const result = await requestData(() => apiGroup().get(`${API_ENDPOINTS.ORDERS}${id}`));
        return result;
    },

    create: async (data) => {
        const result = await requestData(() => apiGroup().post(API_ENDPOINTS.ORDERS, data));
        clearCacheByPrefix(':group:orders');
        return result;
    },

    update: async (id, data) => {
        const result = await requestData(() =>
            apiGroup().patch(`${API_ENDPOINTS.ORDERS}${id}`, data)
        );
        clearCacheByPrefix(':group:orders');
        clearCacheByPrefix(`:group:orders:${id}`);
        return result;
    },

    remove: async (id) => {
        const result = await requestData(() => apiGroup().delete(`${API_ENDPOINTS.ORDERS}${id}`));
        clearCacheByPrefix(':group:orders');
        clearCacheByPrefix(`:group:order:${id}`);
        return result;
    },
};

export const weeklyReportAPI = {
    getAll: (options) =>
        requestWithCache(
            getCacheKey('group', 'report'),
            () => apiGroup().get(API_ENDPOINTS.WEEKLY_REPORT),
            { cacheTime: SHORT_CACHE_TIME, ...options, fallback: [] }
        ),
};

export const customersAPI = {
    getAll: (options) =>
        requestWithCache(
            getCacheKey('group', 'customers'),
            () => apiGroup().get(API_ENDPOINTS.CUSTOMERS),
            { cacheTime: SHORT_CACHE_TIME, ...options, fallback: [] }
        ),

    getById: (uid, options) =>
        requestWithCache(
            getCacheKey('group', `customer:${uid}`),
            () => apiGroup().get(`${API_ENDPOINTS.CUSTOMERS}/${uid}`),
            { ...options }
        ),

    create: async (data) => {
        const result = await requestData(() => apiGroup().post(API_ENDPOINTS.CUSTOMERS, data));
        clearCacheByPrefix(':group:customers');
        return result;
    },

    update: async (uid, data) => {
        const result = await requestData(() =>
            apiGroup().patch(`${API_ENDPOINTS.CUSTOMERS}/${uid}`, data)
        );
        clearCacheByPrefix(':group:customers');
        clearCacheByPrefix(`:group:customer:${uid}`);
        return result;
    },

    remove: async (uid) => {
        const result = await requestData(() =>
            apiGroup().delete(`${API_ENDPOINTS.CUSTOMERS}/${uid}`)
        );
        clearCacheByPrefix(':group:customers');
        clearCacheByPrefix(`:group:customer:${uid}`);
        return result;
    },
};
export const popupAPI = {
    getPopup: (options) =>
        requestWithCache(
            getCacheKey('group', 'popup'),
            () => apiGroup().get(API_ENDPOINTS.NOTIFICATION_POPUP),
            options
        ),
    updatePopup: async (data) => {
        const result = await requestData(() =>
            apiGroup().patch(API_ENDPOINTS.NOTIFICATION_POPUP, data)
        );
        clearCacheByPrefix(':group:popup');
        return result;
    },
};

export const AdminAPI = {
    loginAsUser: async (data) => {
        const result = await requestData(() => apiCore.post(API_ENDPOINTS.ADMIN_LOGIN, data));
        return result;
    },
};

export const productionMethodAPI = {
    getAll: (options) =>
        requestWithCache(
            getCacheKey('group', 'production-methods'),
            () => apiGroup().get(API_ENDPOINTS.PRODUCTION_METHODS),
            {
                cacheTime: SHORT_CACHE_TIME,
                ...options,
                fallback: [],
            }
        ),

    getById: (id, options) =>
        requestWithCache(
            getCacheKey('group', `production-method:${id}`),
            () => apiGroup().get(`${API_ENDPOINTS.PRODUCTION_METHODS}/${id}`),
            {
                cacheTime: SHORT_CACHE_TIME,
                ...options,
            }
        ),

    create: async (data) => {
        const result = await requestData(() =>
            apiGroup().post(API_ENDPOINTS.PRODUCTION_METHODS, data)
        );

        clearCacheByPrefix(':group:production-methods');

        return result;
    },

    update: async (id, data) => {
        const result = await requestData(() =>
            apiGroup().patch(`${API_ENDPOINTS.PRODUCTION_METHODS}/${id}`, data)
        );

        clearCacheByPrefix(':group:production-methods');
        clearCacheByPrefix(`:group:production-method:${id}`);

        return result;
    },

    remove: async (id) => {
        const result = await requestData(() =>
            apiGroup().delete(`${API_ENDPOINTS.PRODUCTION_METHODS}/${id}`)
        );

        clearCacheByPrefix(':group:production-methods');
        clearCacheByPrefix(`:group:production-method:${id}`);

        return result;
    },

    getLines: (methodId, options) =>
        requestWithCache(
            getCacheKey('group', `production-method-lines:${methodId}`),
            () => apiGroup().get(`${API_ENDPOINTS.PRODUCTION_METHOD_LINES}?method=${methodId}`),
            {
                cacheTime: SHORT_CACHE_TIME,
                ...options,
                fallback: [],
            }
        ),

    createLine: async (data) => {
        const result = await requestData(() =>
            apiGroup().post(API_ENDPOINTS.PRODUCTION_METHOD_LINES, data)
        );

        clearCacheByPrefix(':group:production-method-lines');

        return result;
    },

    updateLine: async (id, data) => {
        const result = await requestData(() =>
            apiGroup().patch(`${API_ENDPOINTS.PRODUCTION_METHOD_LINES}/${id}`, data)
        );

        clearCacheByPrefix(':group:production-method-lines');

        return result;
    },

    removeLine: async (id) => {
        const result = await requestData(() =>
            apiGroup().delete(`${API_ENDPOINTS.PRODUCTION_METHOD_LINES}/${id}`)
        );

        clearCacheByPrefix(':group:production-method-lines');

        return result;
    },

    produce: async (id, units) => {
        const result = await requestData(() =>
            apiGroup().post(`${API_ENDPOINTS.PRODUCTION_METHODS}/${id}/produce`, { units })
        );

        clearCacheByPrefix(':group:production-methods');
        clearCacheByPrefix(':group:production-method-lines');
        clearCacheByPrefix(':group:INVENTORY');

        return result;
    },
};

export const itemRecipeAPI = {
    getAll: (options) =>
        requestWithCache(
            getCacheKey('group', 'item-recipes'),
            () => apiGroup().get(API_ENDPOINTS.ITEM_RECIPES),
            {
                cacheTime: SHORT_CACHE_TIME,
                ...options,
                fallback: [],
            }
        ),

    getForItem: (itemId, options) =>
        requestWithCache(
            getCacheKey('group', `item-recipes:${itemId}`),
            () => apiGroup().get(`${API_ENDPOINTS.ITEM_RECIPES}?item=${itemId}`),
            {
                cacheTime: SHORT_CACHE_TIME,
                ...options,
                fallback: [],
            }
        ),

    create: async (data) => {
        const result = await requestData(() => apiGroup().post(API_ENDPOINTS.ITEM_RECIPES, data));

        clearCacheByPrefix(':group:item-recipes');

        return result;
    },

    update: async (id, data) => {
        const result = await requestData(() =>
            apiGroup().patch(`${API_ENDPOINTS.ITEM_RECIPES}/${id}`, data)
        );

        clearCacheByPrefix(':group:item-recipes');

        return result;
    },

    remove: async (id) => {
        const result = await requestData(() =>
            apiGroup().delete(`${API_ENDPOINTS.ITEM_RECIPES}/${id}`)
        );

        clearCacheByPrefix(':group:item-recipes');

        return result;
    },

    deductRecipe: async (itemId, units) => {
        const result = await requestData(() =>
            apiGroup().post(`${API_ENDPOINTS.PRODUCTS}/${itemId}/deduct-recipe`, { units })
        );

        clearCacheByPrefix(':group:item-recipes');
        clearCacheByPrefix(':group:INVENTORY');

        return result;
    },
};
export default apiCore;
